// exportService.js - Export chat to multiple formats
// All exports as NAMED exports (not default)

// Helper: Create download with Save As dialog (if supported)
const downloadFileWithDialog = async (content, filename, mimeType) => {
  // Try modern File System Access API first (Chrome/Edge)
  if ('showSaveFilePicker' in window) {
    try {
      const suggestedName = filename;
      const fileExtension = filename.split('.').pop();
      
      // Define file type
      const opts = {
        suggestedName: suggestedName,
        types: [{
          description: `${fileExtension.toUpperCase()} File`,
          accept: { [mimeType]: [`.${fileExtension}`] }
        }]
      };
      
      // Show Save As dialog
      const handle = await window.showSaveFilePicker(opts);
      const writable = await handle.createWritable();
      await writable.write(content);
      await writable.close();
      
      return; // Success via dialog
    } catch (err) {
      // User cancelled or error - fall back to direct download
      if (err.name !== 'AbortError') {
        console.warn('Save dialog failed, using direct download:', err);
      } else {
        return; // User cancelled, don't download
      }
    }
  }
  
  // Fallback: Direct download (goes to Downloads folder)
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper: Format timestamp
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

// Export as TXT
export const exportAsTXT = async (chat) => {
  let content = `${chat.title}\n`;
  content += `Created: ${formatTimestamp(chat.createdAt)}\n`;
  content += `${'='.repeat(50)}\n\n`;

  chat.messages.forEach(msg => {
    content += `[${msg.role.toUpperCase()}] ${formatTimestamp(msg.timestamp)}\n`;
    content += `${msg.content}\n\n`;
  });

  await downloadFileWithDialog(content, `${chat.title}.txt`, 'text/plain');
};

// Export as JSON
export const exportAsJSON = async (chat) => {
  const content = JSON.stringify(chat, null, 2);
  await downloadFileWithDialog(content, `${chat.title}.json`, 'application/json');
};

// Export as Markdown
export const exportAsMD = async (chat) => {
  let content = `# ${chat.title}\n\n`;
  content += `**Created:** ${formatTimestamp(chat.createdAt)}\n\n`;
  content += `---\n\n`;

  chat.messages.forEach(msg => {
    const role = msg.role === 'user' ? '👤 User' : '🤖 Assistant';
    content += `### ${role} - ${formatTimestamp(msg.timestamp)}\n\n`;
    content += `${msg.content}\n\n`;
  });

  await downloadFileWithDialog(content, `${chat.title}.md`, 'text/markdown');
};

// Export as HTML
export const exportAsHTML = async (chat) => {
  let content = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${chat.title}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      background: #f5f5f5;
    }
    .header {
      background: #2563eb;
      color: white;
      padding: 20px;
      border-radius: 8px;
      margin-bottom: 20px;
    }
    .message {
      background: white;
      padding: 15px;
      margin-bottom: 10px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .user { border-left: 4px solid #2563eb; }
    .assistant { border-left: 4px solid #22c55e; }
    .role {
      font-weight: bold;
      margin-bottom: 5px;
      color: #2563eb;
    }
    .timestamp {
      font-size: 0.85em;
      color: #666;
      margin-bottom: 10px;
    }
    .content {
      white-space: pre-wrap;
      line-height: 1.6;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${chat.title}</h1>
    <p>Created: ${formatTimestamp(chat.createdAt)}</p>
  </div>
`;

  chat.messages.forEach(msg => {
    const role = msg.role === 'user' ? 'User' : 'Assistant';
    content += `
  <div class="message ${msg.role}">
    <div class="role">${role}</div>
    <div class="timestamp">${formatTimestamp(msg.timestamp)}</div>
    <div class="content">${msg.content}</div>
  </div>
`;
  });

  content += `
</body>
</html>`;

  await downloadFileWithDialog(content, `${chat.title}.html`, 'text/html');
};

// Export as CSV
export const exportAsCSV = async (chat) => {
  let content = 'Role,Timestamp,Content\n';

  chat.messages.forEach(msg => {
    const escapedContent = msg.content.replace(/"/g, '""');
    content += `"${msg.role}","${formatTimestamp(msg.timestamp)}","${escapedContent}"\n`;
  });

  await downloadFileWithDialog(content, `${chat.title}.csv`, 'text/csv');
};

// Export as DOCX (using docx library - requires install)
export const exportAsDOCX = async (chat) => {
  try {
    const { Document, Packer, Paragraph, TextRun, HeadingLevel } = await import('docx');

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            text: chat.title,
            heading: HeadingLevel.HEADING_1,
          }),
          new Paragraph({
            text: `Created: ${formatTimestamp(chat.createdAt)}`,
          }),
          new Paragraph({ text: '' }),
          ...chat.messages.flatMap(msg => [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${msg.role.toUpperCase()} - ${formatTimestamp(msg.timestamp)}`,
                  bold: true,
                }),
              ],
            }),
            new Paragraph({
              text: msg.content,
            }),
            new Paragraph({ text: '' }),
          ]),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    
    // Try Save As dialog
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${chat.title}.docx`,
          types: [{
            description: 'Word Document',
            accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (err) {
        if (err.name === 'AbortError') return; // User cancelled
      }
    }
    
    // Fallback: direct download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${chat.title}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('DOCX export requires: npm install docx');
    alert('DOCX export requires the docx library. Using TXT instead.');
    await exportAsTXT(chat);
  }
};

// Export as PDF (using jsPDF - requires install)
export const exportAsPDF = async (chat) => {
  try {
    const { jsPDF } = await import('jspdf');
    
    const doc = new jsPDF();
    let y = 20;

    // Title
    doc.setFontSize(20);
    doc.text(chat.title, 20, y);
    y += 10;

    // Created date
    doc.setFontSize(10);
    doc.text(`Created: ${formatTimestamp(chat.createdAt)}`, 20, y);
    y += 15;

    // Messages
    doc.setFontSize(12);
    chat.messages.forEach(msg => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }

      // Role and timestamp
      doc.setFont(undefined, 'bold');
      doc.text(`${msg.role.toUpperCase()} - ${formatTimestamp(msg.timestamp)}`, 20, y);
      y += 7;

      // Content
      doc.setFont(undefined, 'normal');
      const lines = doc.splitTextToSize(msg.content, 170);
      lines.forEach(line => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(line, 20, y);
        y += 7;
      });
      y += 5;
    });

    // Save with dialog or download
    if ('showSaveFilePicker' in window) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: `${chat.title}.pdf`,
          types: [{
            description: 'PDF Document',
            accept: { 'application/pdf': ['.pdf'] }
          }]
        });
        const pdfBlob = doc.output('blob');
        const writable = await handle.createWritable();
        await writable.write(pdfBlob);
        await writable.close();
        return;
      } catch (err) {
        if (err.name === 'AbortError') return; // User cancelled
      }
    }
    
    // Fallback
    doc.save(`${chat.title}.pdf`);
  } catch (error) {
    console.error('PDF export requires: npm install jspdf');
    alert('PDF export requires the jspdf library. Using TXT instead.');
    await exportAsTXT(chat);
  }
};

// Generic download function
export const downloadAs = (chat, format) => {
  switch(format) {
    case 'txt':
      return exportAsTXT(chat);
    case 'json':
      return exportAsJSON(chat);
    case 'md':
      return exportAsMD(chat);
    case 'html':
      return exportAsHTML(chat);
    case 'csv':
      return exportAsCSV(chat);
    case 'docx':
      return exportAsDOCX(chat);
    case 'pdf':
      return exportAsPDF(chat);
    default:
      return exportAsTXT(chat);
  }
};

// Legacy compatibility exports
export const exportAsText = exportAsTXT;
export const exportToTXT = exportAsTXT;
export const exportToJSON = exportAsJSON;
export { downloadFileWithDialog as downloadFile };
